/**
 * Crear otro negocio con la misma cuenta.
 *
 * Se piden cuatro datos y no diez a propósito: quien ya tiene un negocio
 * funcionando no quiere rellenar otro formulario largo. Todo lo demás —
 * productos, empleados, impuestos— se configura después y cuando haga falta.
 *
 * Al terminar **se entra directo al negocio nuevo**. Crear algo y quedarte
 * donde estabas obliga a buscar el conmutador y adivinar si se creó.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, MessageCircle, Store } from "lucide-react";
import { Boton, Campo, cx, Etiquetado, Lista, Nota } from "@/components/ui";
import { Modal } from "@/components/Modal";
import { mensajeDeError } from "@/lib/api";
import { avisos, negocio } from "@/lib/endpoints";
import { useAuth } from "@/stores/auth";
import { aviso } from "@/stores/ui";

const RUBROS = [
  { valor: "grocery", etiqueta: "Supermercado o bodega" },
  { valor: "barbershop", etiqueta: "Barbería o peluquería" },
  { valor: "carwash", etiqueta: "Autolavado" },
  { valor: "nails", etiqueta: "Manicura y uñas" },
  { valor: "auto_parts", etiqueta: "Venta de repuestos" },
  { valor: "restaurant", etiqueta: "Restaurante o cafetería" },
  { valor: "pharmacy", etiqueta: "Farmacia" },
  { valor: "clothing", etiqueta: "Ropa y calzado" },
  { valor: "services", etiqueta: "Servicios profesionales" },
  { valor: "health", etiqueta: "Consultorio médico o clínica" },
  { valor: "dental", etiqueta: "Consultorio odontológico" },
  { valor: "other", etiqueta: "Otro tipo de negocio" },
];

export function ModalNuevoNegocio({ onCerrar }: { onCerrar: () => void }) {
  const navegar = useNavigate();
  const { cambiarNegocio } = useAuth();

  const [nombre, setNombre] = useState("");
  const [rubro, setRubro] = useState("grocery");
  const [plan, setPlan] = useState<"estandar" | "premium">("estandar");
  const [error, setError] = useState<string | null>(null);

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ AQUÍ YA NO SE PREGUNTA LA MONEDA
    ═══════════════════════════════════════════════════════════════════════════

    Lo vio el dueño: «esto al crear otro negocio no se necesita, si igual se
    hace al entrar». Y era literal — el negocio nuevo entra al asistente de
    bienvenida, y lo PRIMERO que pregunta es «¿en qué moneda están tus
    precios?». La misma pregunta dos veces seguidas, con dos pantallas de por
    medio.

    Preguntar dos veces lo mismo no es solo un paso de más: hace dudar de si la
    segunda vez está pisando la primera, y quien duda contesta distinto.

    En su lugar va lo que SÍ hay que decidir antes de crear: con qué plan
    arranca. Ese no lo pregunta nadie después.
  */
  const { data: contacto } = useQuery({
    queryKey: ["asesor"],
    queryFn: avisos.asesor,
    staleTime: Infinity,
    retry: false,
  });

  // ── Crear negocios NO está capado por plan, y es a propósito ───────────────
  //
  // Bloquearlo no protegía ni un dólar: cada negocio paga su propia suscripción
  // igual. Lo único que conseguía era empujar a la gente a abrirse otro correo y
  // registrarse como si fuera otra persona — y ahí perdíamos lo único que sí
  // vale, que es saber que los dos negocios son del mismo dueño.
  //
  // Lo que sí es de Premium es **verlos juntos** (`/reportes` consolidado).
  // Tener varios es gratis; compararlos sin sumar a mano es lo que se paga.

  const crear = useMutation({
    mutationFn: () =>
      negocio.crear({
        nombre: nombre.trim(),
        business_type: rubro,
      }),
    // ═══════════════════════════════════════════════════════════════════════
    //  LA PANTALLA EN BLANCO AL CREAR UN NEGOCIO — QUÉ PASABA DE VERDAD
    // ═══════════════════════════════════════════════════════════════════════
    //
    // Antes esto navegaba siempre a "/" después de cambiar de negocio, y había
    // que recargar a mano para ver algo. El motivo no es obvio y conviene
    // dejarlo escrito, porque es una trampa de React Router que se puede
    // repetir en cualquier otro sitio:
    //
    //   1. Un negocio recién creado tiene el wizard pendiente, así que el
    //      guardia de ruta responde con `<Navigate to="/bienvenida" replace/>`.
    //   2. `<Navigate>` no navega al dibujarse: lo hace en un efecto, **una
    //      sola vez**. Sus dependencias son el destino y las opciones, y esas
    //      no cambian nunca.
    //   3. Nuestro `navegar("/")` llegaba justo después y devolvía la URL a "/".
    //   4. El guardia volvía a dibujar EL MISMO `<Navigate>`. Su efecto ya
    //      había corrido, así que no volvió a dispararse — y `<Navigate>`
    //      dibuja `null`.
    //
    // Resultado: la aplicación entera pintando `null`, sin ningún error en la
    // consola. Una pantalla negra silenciosa.
    //
    // El arreglo es no pelearse con el guardia: se le pregunta a la sesión
    // nueva a dónde toca ir, y se va ahí directamente.
    onSuccess: async (nuevo) => {
      onCerrar();

      let sesion;
      try {
        sesion = await cambiarNegocio(nuevo.id);
      } catch {
        // Si el cambio falla, el negocio SÍ se creó. Se le dice para que no
        // vuelva a crearlo pensando que no funcionó.
        aviso.exito("Se creó", "Cámbiate a él desde el nombre del negocio, arriba.");
        return;
      }

      // `replace` y no un empujón al historial: volver atrás desde el negocio
      // nuevo llevaría a una pantalla del negocio anterior, que ya no tiene
      // datos cargados y confunde más de lo que ayuda.
      navegar(sesion.needs_onboarding ? "/bienvenida" : "/", { replace: true });

      /*
        ⚠️ EL AVISO AL ASESOR VA DESPUÉS DE ENTRAR, NO ANTES.

        Se abre en una pestaña nueva, y algunos navegadores la bloquean si
        llega en medio de otra navegación. Y si el bloqueo pasa, lo importante
        —que el negocio quedó creado y funcionando— no puede depender de eso:
        el aviso de abajo lo dice igual, con el enlace a mano.
      */
      if (plan === "premium" && contacto?.whatsapp) {
        window.open(contacto.whatsapp, "_blank", "noopener");
      }

      aviso.exito(
        `${nuevo.name} está listo`,
        plan === "premium"
          ? "Ya puedes vender. Le avisamos a tu asesor para activar Premium."
          : "Ya puedes cargar productos y vender.",
      );
    },
    onError: (e) => setError(mensajeDeError(e)),
  });

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo="Crear otro negocio"
      descripcion="Con su propio inventario, su caja y sus reportes."
      ancho="sm"
      pie={
        <>
          <Boton variante="secundario" onClick={onCerrar} disabled={crear.isPending}>
            Cancelar
          </Boton>
          <Boton
            onClick={() => {
              setError(null);
              crear.mutate();
            }}
            cargando={crear.isPending}
            disabled={nombre.trim().length < 2}
          >
            Crear negocio
          </Boton>
        </>
      }
    >
      <div className="space-y-4">
        <Etiquetado label="¿Cómo se llama?" requerido>
          <Campo
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Mi Kiosco"
            autoFocus
            maxLength={160}
          />
        </Etiquetado>

        <Etiquetado label="¿A qué se dedica?" ayuda="Sirve para sugerirte productos al empezar.">
          <Lista value={rubro} onChange={(e) => setRubro(e.target.value)}>
            {RUBROS.map((r) => (
              <option key={r.valor} value={r.valor}>
                {r.etiqueta}
              </option>
            ))}
          </Lista>
        </Etiquetado>

        {/*
          ═══════════════════════════════════════════════════════════════════
           CON QUÉ PLAN ARRANCA
          ═══════════════════════════════════════════════════════════════════

          Lo que sí hay que decidir antes de crear, y que no pregunta nadie
          después. Elegir Premium aquí NO cobra ni activa nada: abre WhatsApp
          con el asesor, que es quien procesa el pago. Decirlo con esas
          palabras evita que alguien cree el negocio creyendo que ya lo tiene.
        */}
        <Etiquetado label="¿Con qué plan arranca?">
          <div className="grid gap-2 sm:grid-cols-2">
            <OpcionDePlan
              elegido={plan === "estandar"}
              onElegir={() => setPlan("estandar")}
              titulo="Estándar"
              texto="Un local, una persona. Vender, inventario, caja y crédito."
            />
            <OpcionDePlan
              elegido={plan === "premium"}
              onElegir={() => setPlan("premium")}
              titulo="Premium"
              texto="Equipo con permisos, varios locales y todos tus negocios en una pantalla."
            />
          </div>
        </Etiquetado>

        {plan === "premium" && (
          <Nota tono="neutro" icono={<MessageCircle size={14} />}>
            Al crearlo <strong>le escribimos a tu asesor</strong> para que active Premium y
            cuadre el pago contigo. El negocio queda listo para vender desde ya — Premium se
            enciende encima, sin tocar nada de lo que hayas cargado.
          </Nota>
        )}

        {/*
          Se dice AQUÍ, antes de crear, que el negocio nuevo no trae prueba
          gratis. Enterarse después —cuando ya cargaste cincuenta productos y te
          pide pagar— se siente a trampa aunque no lo sea.

          Y no trae prueba a propósito: si cada negocio nuevo estrenara treinta
          días gratis, nadie pagaría nunca. Bastaría con crear uno cada mes.
        */}
        <Nota tono="neutro" icono={<Store size={14} />}>
          El negocio nuevo nace <strong>separado del actual</strong>: sus productos, su caja y
          sus reportes no se mezclan con los de aquí. La prueba gratis es una sola por
          persona, así que este empieza con su propia suscripción.
        </Nota>

        {error && <Nota tono="peligro">{error}</Nota>}
      </div>
    </Modal>
  );
}

/**
 * Una opción de plan, como tarjeta y no como fila de un desplegable.
 *
 * ⚠️ Con un `<select>` la diferencia entre los dos planes sería un nombre, y
 * nadie elige por un nombre: hay que ver qué trae cada uno. Dos tarjetas caben
 * en el mismo sitio y sí lo dicen.
 */
function OpcionDePlan({
  elegido,
  onElegir,
  titulo,
  texto,
}: {
  elegido: boolean;
  onElegir: () => void;
  titulo: string;
  texto: string;
}) {
  return (
    <button
      type="button"
      onClick={onElegir}
      className={cx(
        "rounded-[var(--radius-control)] border p-3 text-left transition-colors",
        elegido
          ? "border-accent bg-accent-soft"
          : "border-line bg-surface-2 hover:border-line-strong",
      )}
    >
      <span className="flex items-center gap-1.5">
        {elegido && <Check size={14} className="shrink-0 text-accent" />}
        <span className={cx("text-sm font-semibold", elegido ? "text-accent" : "text-ink")}>
          {titulo}
        </span>
      </span>
      <span className="mt-1 block text-xs leading-relaxed text-muted">{texto}</span>
    </button>
  );
}
